// BookingConciergePremiumConsultation.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { FORMSPREE_ENDPOINT } from '../../data/constants';
import StepIndicator from './components/StepIndicator';
import ProjectTypeSelector from './components/ProjectTypeSelector';
import IntakeForm from './components/IntakeForm';
import ConsultationCalendar from './components/ConsultationCalendar';
import BudgetDiscussion from './components/BudgetDiscussion';
import BookingConfirmation from './components/BookingConfirmation';
import Icon from '../../components/AppIcon';

const BookingConciergePremiumConsultation = () => {
  const location = useLocation();
  const preselectedProject = location.state?.preselectedProject || null;

  // Initialize currentStep based on preselected project
  const [currentStep, setCurrentStep] = useState(preselectedProject ? 2 : 1);
  const [selectedProjectType, setSelectedProjectType] = useState(preselectedProject);

  const [formData, setFormData] = useState({
    projectType: preselectedProject?.id || '',
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    projectScope: '',
    timeline: '',
    budget: '',
    objectives: '',
    targetAudience: '',
    specificRequirements: '',
    consultationType: 'virtual',
    preferredDate: '',
    preferredTime: ''
  });

  const [showMobileSelector, setShowMobileSelector] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');

  const handleProjectTypeSelect = (projectType) => {
    setSelectedProjectType(projectType);
    setFormData(prev => ({ ...prev, projectType: projectType.id }));
    setCurrentStep(2);
  };

  const handleFormUpdate = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = async () => {
    if (currentStep === 4) {
      try {
        const confNum = `MC-${Date.now().toString().slice(-6)}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
        setConfirmationNumber(confNum);

        await submitToFormspree(confNum);
        setCurrentStep(5);
      } catch {
        alert('Submission failed. Check console.');
      }
      return;
    }

    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitToFormspree = async (confirmationNumber) => {
    const payload = {
      name: formData.contactName,
      email: formData.email,
      _replyto: formData.email,
      phone: formData.phone,
      projectType: selectedProjectType?.title || formData.projectType,
      consultationType: formData.consultationType,
      companyName: formData.companyName,
      projectScope: formData.projectScope,
      objectives: formData.objectives,
      timeline: formData.timeline,
      budget: formData.budget,
      targetAudience: formData.targetAudience,
      specificRequirements: formData.specificRequirements,
      preferredDate: formData.preferredDate,
      preferredTime: formData.preferredTime,
      confirmationNumber
    };

    const res = await fetch(FORMSPREE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const data = await res.json();
      console.error('Formspree error:', data);
      throw new Error('Submission failed');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProjectTypeSelector
            onSelect={handleProjectTypeSelect}
            selectedType={selectedProjectType}
            showMobileSelector={showMobileSelector}
            setShowMobileSelector={setShowMobileSelector}
          />
        );
      case 2:
        return (
          <IntakeForm
            projectType={selectedProjectType}
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <ConsultationCalendar
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
          />
        );
      case 4:
        return (
          <BudgetDiscussion
            projectType={selectedProjectType?.id}
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
          />
        );
      case 5:
        return (
          <BookingConfirmation
            formData={formData}
            projectType={selectedProjectType}
            confirmationNumber={confirmationNumber}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const duration = 700; 
    const start = window.scrollY;
    const startTime = performance.now();

    const easeOutCubic = t => 1 - Math.pow(1 - t, 3);

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      window.scrollTo(0, start * (1 - eased));

      if (progress < 1) requestAnimationFrame(animateScroll);
    };

    requestAnimationFrame(animateScroll);
  }, [currentStep]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <section data-theme="dark" className="relative py-8 sm:py-20 bg-gradient-to-br from-primary via-red-900 to-secondary overflow-hidden">
        <div className="absolute inset-0 bg-black/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-6xl mt-12 font-bold text-white mb-2 leading-tight">
              Let's Craft Your
              <span className="text-gradient-gold block">Cinematic Vision</span>
            </h1>
            <p className="text-base sm:text-xl text-white max-w-3xl mx-auto leading-relaxed">
              Begin your journey with a personalized consultation.
            </p>
          </div>
          <StepIndicator currentStep={currentStep} />
        </div>
      </section>

      <section data-theme="light" className="py-4 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-premium p-4 md:p-12">
            {renderStepContent()}

            {currentStep > 1 && currentStep < 5 && (
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-border">
                <button
                  onClick={handlePrevious}
                  className="flex items-center justify-center lg:space-x-2 px-6 py-3 text-xs font-semibold uppercase text-black transition-command sm:text-sm bg-white/10 backdrop-blur-command rounded-lg border border-black/20 cinematic-transition"
                >
                  <Icon name="ArrowLeft" size={20} strokeWidth={2} />
                  <span>Previous</span>
                </button>

                <div className="text-sm text-text-secondary hidden sm:block">
                  Step {currentStep} of 5
                </div>

                {currentStep < 4 && (
                  <button
                    onClick={handleNext}
                    className="flex items-center justify-center lg:space-x-2 px-6 py-3 text-xs font-semibold uppercase text-white transition-command sm:text-sm bg-red-700 rounded-lg shadow-emergency hover:bg-accent/90 cinematic-transition cta-glow"
                  >
                    <span>Next</span>
                    <Icon name="ArrowRight" size={20} strokeWidth={2} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section data-theme="dark" className="py-4 sm:py-16 bg-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-red-600 mb-2">
            Need Assistance?
          </h3>
          <p className="text-base text-text-secondary mb-2 sm:mb-8">
            Our consultation specialists are here to help you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a
              href="tel:+254740000444"
              className="flex items-center space-x-2 text-accent hover:text-accent-dark font-medium cinematic-transition"
            >
              <Icon name="Phone" size={20} strokeWidth={2} />
              <span>+254 (740) 000 444</span>
            </a>
            <a
              href="mailto:info@hype.co.ke"
              className="flex items-center space-x-2 text-accent hover:text-accent-dark font-medium cinematic-transition"
            >
              <Icon name="Mail" size={20} strokeWidth={2} />
              <span>info@hype.co.ke</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingConciergePremiumConsultation;
